import * as yup from "yup";

import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmailFieldRHF,
	EnhancedDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedMultiDropDownRHFWithLabel,
	EnhancedSnackbar,
	ExtensionNumberRHF,
	IEnhancedDropDownItem,
	IResponse,
	IServerError,
	ITest,
	LabelPosition,
	PhoneNumberRHF,
	PostCodeRHF,
	RHookFormDatePicker,
	RHookFormMaskedTextFieldWithLabel,
	RHookFormTextFieldWithLabel,
	SinNumberRHF,
	SingleColumnLayout,
	StandardPanel,
	StandardSubmitFormButtonsToolbar,
} from "@websential/cosmic-v20";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
	FILING_RESOURCE_SETUP_SCREEN_SLUG,
	GET_QUERY_KEY,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	QUERY_OPTIONS,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FilingResourceSetupSchema, ROLE_TRANSMITTER, hasRole } from "./validator";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { checkSINValidity, scrollPanelFieldsToTop, sortDDLByLabel, useNavigateToLogout } from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../../DDLDataTable";
import { EmployeeService } from "../../../services/EmployeeService";
import { EnhancedButtonPrimeReact } from "../../EnhancedButtonPrimeReact";
import { FilingResourceService } from "../../../services/FilingResourceService";
import { FilingResourceSetupToolbar } from "./FilingResourceSetupToolbar";
import { IAppState } from "../../../store/reducers";
import { ICountryReferenceModel } from "../../../models/common/ICountryReferenceModel";
import { IFilingResourceRequestPayload } from "../../../models/filingResource/IFilingResourceRequestPayload";
import { IGetListRequest } from "../../../models";
import { IProvinceReferenceModel } from "../../../models/province/IProvinceReferenceModel";
import { ProvinceService } from "../../../services/common/ProvinceService";
import { YearService } from "../../../services/YearService";
import { instanceOfIFillingResourceResponce } from "../../../common/instance-method";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IFilingResourceSetup extends ITest { }
const SINGLE_RECORD_DATA_QUERY_KEY = "filling-resource-single-data-record";

// const useGetData = (id: string | undefined, clientId: string) => {
//   return useQuery(
//     [SINGLE_RECORD_DATA_QUERY_KEY, id],
//     () => FilingResourceService.getById(id, clientId),
//     QUERY_OPTIONS
//   );
// };

const useGetData = (id: string | undefined, clientId: string) =>
	useQuery({
		queryKey: [`single-filing-resource-data`, id, clientId],
		queryFn: (data) => {
			return FilingResourceService.getById(id, clientId);
		},
		refetchOnWindowFocus: false,
	});

const FILLING_RESOURCE_LIST_QUERY_KEY = "filing-resource-list";

const useFilingResources = (clientId: string, params: IGetListRequest) =>
	useQuery({
		queryKey: ["filing-resource-list", params],
		queryFn: (data) => {
			return FilingResourceService.getList(clientId, params);
		},
		refetchOnWindowFocus: false,
	});

const contactPersonId = 1;
const issuerId = 2;
const transmitterId = 3;
// export let issuerSelectedValidate = false;

//years list from API
const useYears = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["years-list", params],
		queryFn: (data) => {
			return YearService.getList(params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";
const useGetEmployeeDDLItems = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

export let currentTaxYear = new Date().getFullYear();

export const FilingResourceSetup: FC<IFilingResourceSetup> = ({ testId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	// RESPONSIVE
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const isPendingActivityLogData = useSelector(
		(state: IAppState) => state.isLoadAxiosCall.isLoading
	);
	currentTaxYear = taxYear;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
	const mobileTabOrSmallLaptopScreen = isMobile || isSmallScreen;
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [filingResourceId, setFilingResourceId] = useState<number | string>(0);
	const [openDialog, setDialogOpen] = useState(false);
	const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
	const [employeeData, setEmployeeData] = useState<any>(undefined);

	const [issuerSelected, setIssuerSelected] = useState<boolean>(false);
	const [firstNameDisabled, setFirstNameDisabled] = useState<boolean>(false);
	const [selectedProvince, setSelectedProvince] =
		useState<IProvinceReferenceModel>({
			name: "",
			shortCode: "",
		});
	// const dashboardApiData = useSelector((state: IAppState) => state.dashboardApisData)

	const [CountryResponse] = useState<ICountryReferenceModel>({
		id: 1,
		name: "Canada",
		shortCode: "CAN",
	});

	// for edit
	const queryClient = useQueryClient();
	const { id } = useParams();
	const [editRecordId, setEditRecordId] = useState(id);


	const [transmitterSelected, setTransmitterSelected] =
		useState<boolean>(false);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const DEFAULT_VALUES = {
		role: ["6173ddf7-68e2-426f-b1be-9d1dd63eb49c"],
		effectiveFromYear: undefined,
		firstName: undefined,
		lastName: undefined,
		phone1: undefined,
		ext: undefined,
		ownerSin1: undefined,
		ownerSin2: undefined,
		transmitterAccountNumber: undefined,
		// craTransmitter: undefined,
		representativeId: undefined,
		rqTransmitter: undefined,
		rl1Type: undefined,
		address: undefined,
		city: undefined,
		province: undefined,
		country: "Canada",
		postCode: undefined,
		contactName: undefined,
		email: undefined,
	};
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const { isPending: isSubmitting, mutate: submitFormData } = useMutation({
		mutationFn: FilingResourceService.create,

		onSuccess: (
			res: IFilingResourceRequestPayload | IServerError | IResponse | any
		) => {
			if (instanceOfIFillingResourceResponce(res)) {
				queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.id],
				});
			}
			if (res.status === 201 || res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				queryClient.invalidateQueries({
					queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, res.data.id],
				});
				queryClient.invalidateQueries({
					queryKey: [FILLING_RESOURCE_LIST_QUERY_KEY],
				});
				setEditRecordId(res.data.id)
				scrollPanelFieldsToTop();
				let path = `/company/filing-resources/setup/${res.data.id}`;
				navigate(path);
				setFirstNameDisabled(false);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.data.message && typeof res.data.message !== "object"
							? res.data.message
							: res.data.message.message
								? res.data.message.message
								: "Data has been submitted successfully!",
					severity:
						res.data.status === false
							? "error"
							: res.data.success === false
								? "error"
								: "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "An error occurred, please try again",
				severity: "error",
			});
		},
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending: isPendingFilingResourceList, data: FilingResourceList } =
		useFilingResources(clientId, queryParams);

	const upadtedFilingResourceListItems = useMemo(() => {
		return (
			FilingResourceList &&
			FilingResourceList.items &&
			FilingResourceList?.items.map((item: any) => ({
				...item,
				role: item.role.join(", "),
				label: (
					<Link
						to={`/company/filing-resources/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.name}
					</Link>
				),
			}))
		);
	}, [FilingResourceList]);

	const onFilingResourceSelectionchangeHandler = (item: any) => {
		if (item.value) {
			setEditRecordId(item.value);
			navigate(`/company/filing-resources/setup/${item.value}`);
		} else {
			setEditRecordId(undefined);
			navigate(`/company/filing-resources/setup`);
			formMethods.reset();
			formMethods.reset(DEFAULT_VALUES);
			formMethods.setValue('email', undefined)
			setIssuerSelected(false)
			setTransmitterSelected(false)
			scrollPanelFieldsToTop();
			setFirstNameDisabled(false);
		}
	}

	const upadtedFilingResourceDDLItems = useMemo(() => {
		return (
			FilingResourceList &&
			FilingResourceList.items &&
			FilingResourceList?.items.map((item: any) => ({
				label: item.name,
				value: item.id
			}))
		);
	}, [FilingResourceList]);

	// for edit
	const { isPending: isPendingData, data: selectedFillingResourceData } =
		useGetData(editRecordId, clientId);

	//get years list by API
	const navigateToLogout = useNavigateToLogout();
	const { isPending, data } = useYears(
		{
			search_query: "",
			order_by: "role_name",
			order_direction: "asc",
			page: 1,
			size: INITIAL_PAGE_SIZE,
		},
		navigateToLogout
	); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const YearsGetList: any = data;

	const { isPending: isPendingEmployeeDDLItems, data: employeeDDLItems } =
		useGetEmployeeDDLItems(clientId, taxYear);


	const yearItems = useMemo(() => {
		if (YearsGetList) {
			return YearsGetList.items.map((yearItem: any) => ({
				label: String(yearItem.taxYear),
				value: yearItem.taxYear,
			}));
		}
	}, [YearsGetList]);

	const ExtendedValidationSchema = FilingResourceSetupSchema.shape({
		craTransmitter: yup
			.string()
			.test(
				"craTransmitter-test",
				"CRA Transmitter is required",
				function (value) {
					const { role } = this.parent;

					if (taxYear <= 2023 && hasRole(role, ROLE_TRANSMITTER)) {

						if (value && value.startsWith("M") && value.length < 8) {
							return this.createError({
								message: "CRA Transmitter is required Minimum 8 characters",
							});
						} else if (!value || value.length < 6) {
							return this.createError({
								message: "CRA Transmitter is required Minimum 8 characters",
							});
						}
					}

					return true;
				}
			),
		transmitterAccountNumber: yup
			.string()
			.nullable()
			.when("role", (role: any, schema) => {
				if (
					Array.isArray(role) &&
					role.includes("6173ddf7-68e2-426f-b1be-9d1dd63eb49e")
				) {
					return schema.test(
						"is-valid-or-not",
						"Invalid Account Number",
						function (value) {
							if (value) {
								const maxLength = 15;
								const firstNineDigits = value.substring(0, 9);
								const output = checkSINValidity(firstNineDigits);

								if (value.length === maxLength && output % 10 === 0) {
									return true;
								} else {
									return this.createError({
										message: "Invalid Account Number",
										path: this.path,
									});
								}
							}
							return true;
						}
					);
				}
				return schema.nullable();
			}),

		representativeId: yup
			.string()
			.nullable()
			.when("role", (role: any, schema) => {
				if (
					Array.isArray(role) &&
					role.includes("6173ddf7-68e2-426f-b1be-9d1dd63eb49e")
				) {
					return schema.max(7, "Representative Id has at most 7 characters");
				}
				return schema.nullable();
			}),
	});

	const formMethods: any = useForm<any>({
		resolver: yupResolver(ExtendedValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		// defaultValues: DEFAULT_VALUES,
	});

	const onSubmit = (formData: any) => {


		//province is in string format and by using this we convert that in object
		const jsonString =
			formData.province !== undefined && ""
				? formData.province
				: ('{id:9, name: "Ontario",shortCode:"ON"}' as string);
		// Custom parsing function to convert the JSON-like string to an object
		const parseJSONLikeString = (str: string) => {
			// Use regular expressions to add double quotes around keys and string values
			const modifiedString = str.replace(/(\w+):/g, '"$1":').replace(/'/g, '"');
			// Convert the modified JSON string to an object using JSON.parse()
			return JSON.parse(modifiedString);
		};
		// Convert the JSON-like string to an object
		const provinceObject = parseJSONLikeString(jsonString);


		const maskedStatus: any = sessionStorage.getItem("maskEnabled");


		const requestPayload: IFilingResourceRequestPayload = {
			// clientId: formData.clientId,
			clientId: clientId,
			id: selectedFillingResourceData
				? selectedFillingResourceData.id
				: undefined,
			// name: formData.name,
			firstName: formData.firstName,
			lastName: formData.lastName,
			effectiveFromYear: parseInt(formData.effectiveFromYear),
			phone1: formData.phone1 && formData.phone1.replace(/\D/g, ""),
			ext: formData.ext !== "" ? formData.ext : undefined,
			role: formData.role,
			ownerSin1:
				selectedFillingResourceData &&
					selectedFillingResourceData.id &&
					maskedStatus === "true"
					? "000000000"
					: formData.ownerSin1 && issuerSelected
						? formData.ownerSin1.replace(/-/g, "")
						: undefined,
			ownerSin2:
				formData.ownerSin2 !== "" && issuerSelected
					? formData.ownerSin2 && formData.ownerSin2.replace(/-/g, "")
					: undefined,
			transmitterAccountNumber:
				taxYear > 2023 &&
					transmitterSelected &&
					formData.transmitterAccountNumber
					? formData.transmitterAccountNumber
					: undefined,
			representativeId:
				taxYear > 2023 && transmitterSelected && formData.representativeId
					? formData.representativeId
					: undefined,
			// craTransmitter:
			// 	formData.craTransmitter &&
			// 		transmitterSelected &&
			// 		formData.craTransmitter.length === 6
			// 		? `MM${formData.craTransmitter}`
			// 		: transmitterSelected && formData.craTransmitter.length === 8
			// 			? `${formData.craTransmitter}`
			// 			: undefined,
			craTransmitter: formData.craTransmitter && transmitterSelected && taxYear <= 2023
				&& formData.craTransmitter.length === 6
				? `MM${formData.craTransmitter}` : formData.craTransmitter && transmitterSelected && taxYear <= 2023 && formData.craTransmitter.length === 8
					? `${formData.craTransmitter}` : undefined,
			rqTransmitter:
				formData.rqTransmitter !== "" &&
					formData.rqTransmitter !== undefined &&
					transmitterSelected &&
					formData.rqTransmitter.length === 6
					? `NP${formData.rqTransmitter}`
					: transmitterSelected && formData.rqTransmitter && formData.rqTransmitter.length === 8
						? `${formData.rqTransmitter}`
						: undefined,
			rl1Type:
				formData.rl1Type !== "" &&
					formData.rl1Type !== undefined &&
					transmitterSelected
					? formData.rl1Type
					: undefined,
			address:
				formData.address && transmitterSelected ? formData.address : undefined,
			city: formData.city && transmitterSelected ? formData.city : undefined,
			postCode:
				formData.postCode && transmitterSelected
					? formData.postCode.replace(/[a-z]/g, (match: string) =>
						match.toUpperCase()
					)
					: undefined,
			contactName:
				formData.contactName && transmitterSelected
					? formData.contactName
					: undefined,
			email: formData.email && transmitterSelected ? formData.email : undefined,
			// province: formData.province,
			province: transmitterSelected ? provinceObject : undefined,
			country: transmitterSelected ? CountryResponse : undefined,
		};


		if (
			taxYear > 2023 &&
			transmitterSelected &&
			!requestPayload.transmitterAccountNumber &&
			!requestPayload.representativeId
		) {
			setSnackbarData({
				open: true,
				message:
					"Either the Transmitter Account Number or Representative ID is required.",
				severity: "error",
			});
		} else {
			submitFormData(requestPayload);
		}
	};

	const { isPending: isDeleting, mutate: deleteFilingResourceById } =
		useMutation({
			mutationFn: FilingResourceService.delFilingResource,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the FilingResource fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				scrollPanelFieldsToTop();
				let path = `/company/filing-resources/setup`;
				navigate(path);
				setFirstNameDisabled(false);
				queryClient.invalidateQueries({
					queryKey: [FILLING_RESOURCE_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteFilingResourceField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				filingResourceId: filingResourceId,
				clientId: clientId,
				navigateToLogout: navigateToLogout,
			};
			deleteFilingResourceById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedFillingResourceData) {
			setFilingResourceId(selectedFillingResourceData.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setFilingResourceId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveFilingResourceById } =
		useMutation({
			mutationFn: FilingResourceService.archiveFilingResource,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the FilingResource fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/company/filing-resources/setup`;
				navigate(path);
				setFirstNameDisabled(false);
				return queryClient.invalidateQueries({
					queryKey: [FILLING_RESOURCE_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveFilingResourceField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				filingResourceId: filingResourceId,
				navigateToLogout: navigateToLogout,
			};
			archiveFilingResourceById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedFillingResourceData) {
			setFilingResourceId(selectedFillingResourceData.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/company/filing-resources/setup/${params.id}`;
		navigate(path);
	}, []);

	const initialState = () => {
		formMethods.reset(DEFAULT_VALUES);
		setIssuerSelected(false)
		setTransmitterSelected(false)
		setEmployeeData(undefined)
		let path = `/filing-resources/setup`;
		navigate(path);
	}
	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		formMethods.reset(DEFAULT_VALUES);
		formMethods.setValue('email', undefined)
		setIssuerSelected(false)
		setTransmitterSelected(false)
		scrollPanelFieldsToTop();
		let path = `/company/filing-resources/setup`;
		navigate(path);
		setFirstNameDisabled(false);
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const onSelectionChangeHandler = (selectedItems: IEnhancedDropDownItem[]) => {

		if (selectedItems.length === 1) {
			if (selectedItems.find((item) => item.id === 1)) {
				setIssuerSelected(false);
				setTransmitterSelected(false);
			} else if (selectedItems.find((item) => item.id === 2)) {
				setIssuerSelected(true);
				setTransmitterSelected(false);
			} else if (selectedItems.find((item) => item.id === 3)) {
				// setIssuerSelected(true);
				setIssuerSelected(false);
				setTransmitterSelected(true);
			}
		} else if (selectedItems.length === 2) {
			if (
				selectedItems.find((item) => item.id === 1) &&
				selectedItems.find((item) => item.id === 2)
			) {

				setIssuerSelected(true);
				setTransmitterSelected(false);
			} else if (
				selectedItems.find((item) => item.id === 1) &&
				selectedItems.find((item) => item.id === 3)
			) {

				// setIssuerSelected(true);
				setIssuerSelected(false);
				setTransmitterSelected(true);
			} else if (
				selectedItems.find((item) => item.id === 2) &&
				selectedItems.find((item) => item.id === 3)
			) {

				setIssuerSelected(true);
				setTransmitterSelected(true);
			}
		} else if (selectedItems.length === 3) {
			if (selectedItems.find((item) => item.id === 1 && 2 && 3)) {
				setIssuerSelected(true);
				setTransmitterSelected(true);
			}
		} else if (selectedItems.length === 0) {
			setIssuerSelected(false);
			setTransmitterSelected(false);
		}
		// setIssuerSelected(
		//   selectedItems.findIndex((obj) => obj.id === issuerId || transmitterId) !== -1
		// );

		// setTransmitterSelected(
		//   selectedItems.findIndex((obj) => obj.id === transmitterId) !== -1
		// );
	};


	const useGetProvinceDDL = () => {
		return useQuery({
			queryKey: [GET_QUERY_KEY.PROVINCE_DDL],
			queryFn: () => ProvinceService.get(),
			...QUERY_OPTIONS,
		});
	};

	const { isPending: isPendingProvinceDropDownList, data: provinceList } =
		useGetProvinceDDL();

	// const onProvinceSelectionChangeHandler = (item: IEnhancedDropDownItem) => {
	//   setSelectedProvince({
	//     name: item.label,
	//     shortCode: String(item.value),
	//   });
	// };

	// for edit
	useEffect(() => {
		// if the selected flex field data has loaded.
		if (selectedFillingResourceData) {
			setFirstNameDisabled(true);
			Object.keys(selectedFillingResourceData).forEach((fieldName) => {

				// @ts-ignore
				const fieldValue = selectedFillingResourceData[fieldName];

				// @ts-ignore
				if (typeof fieldValue !== "object") {
					// @ts-ignore
					formMethods.setValue(
						fieldName,
						fieldValue && fieldValue !== null ? fieldValue : undefined
					);
				}
				if (fieldName === "role") {
					// @ts-ignore
					formMethods.setValue(fieldName, fieldValue ?? undefined);
					if (fieldValue.includes("6173ddf7-68e2-426f-b1be-9d1dd63eb49d")) {
						setIssuerSelected(true);
					}
					if (fieldValue.includes("6173ddf7-68e2-426f-b1be-9d1dd63eb49e")) {
						setTransmitterSelected(true);
					}
				}
				if (fieldName === "province") {
					let id = fieldValue.id;
					let name = fieldValue.name;
					let shortCode = fieldValue.shortCode;


					formMethods.setValue(
						fieldName,
						// '{id:9, name: "Ontario",shortCode:"ON"}'
						`{id:${id}, name: "${name}",shortCode:"${shortCode}"}`
					);
				}
				if (fieldName === "country") {
					// if (objFieldValue.name === "Canada") {
					formMethods.setValue(fieldName, fieldValue.name ?? undefined);
					// }
				}
				if (fieldName === "ownerSin1") {
					const formattedNumber = fieldValue
						? fieldValue.replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3")
						: undefined;
					formMethods.setValue("ownerSin1", formattedNumber);
				}
				if (fieldName === "ownerSin2") {
					const formattedNumber = fieldValue
						? fieldValue.replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3")
						: undefined;
					formMethods.setValue("ownerSin2", formattedNumber);
				}
				if (fieldName === "phone1") {
					const formattedNumber = fieldValue
						? fieldValue.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
						: undefined;
					formMethods.setValue("phone1", formattedNumber);
				}
			});
		}
	}, [selectedFillingResourceData, formMethods.setValue, formMethods]);



	const onEmployeeSelectionChangeHandler = async (selectedItem: any) => {

		if (selectedItem.value !== "") {
			(async () => {
				try {
					setIsLoadingData(true)
					const response: any = await EmployeeService.getById(selectedItem.value, navigateToLogout);
					if (response) {
						setEmployeeData(response)
						formMethods.setValue('name', `${response.name1} ${response.name2}`)
						formMethods.setValue('address', response.address[0].addressLine1)
						formMethods.setValue('ownerSin1', response.uniqueIdentifier)
						formMethods.setValue('email', response.email)
						formMethods.setValue('city', response.address[0].city)
						formMethods.setValue('postCode', response.address[0].postalCode)
						formMethods.setValue('contactName', `${response.name1} ${response.name2}`)
						const phoneNo = response.metaFields && response.metaFields.find((x: any) => x.name === "phoneNo")

						if (phoneNo) {
							formMethods.setValue('phone1', phoneNo.value)
						}
						if (response.address[0].province) {
							let id = response.address[0].province.id;
							let name = response.address[0].province.name;
							let shortCode = response.address[0].province.shortCode;


							formMethods.setValue(
								"province",
								`{id:${id}, name: "${name}",shortCode:"${shortCode}"}`
							);
						}
					}
				}
				catch { } finally { setIsLoadingData(false) }
			})();
		} else {
			initialState()
		}
	}


	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const expandedLabels = ["Company", "Filing Resource"];
	const expandedFilingResourcesSetupOrSetupItem = "Filing Resource"; // Special case for "Setup" under "FilingResources"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Company" &&
				item.label === expandedFilingResourcesSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedFilingResourcesSetupOrSetupItem && "/company/filing-resources/setup",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: id ? id : "",
				entityId2: "",
				entityId3: "",
				screenName: FILING_RESOURCE_SETUP_SCREEN_SLUG,
				companyName: clientName,
				activityType: "activity_type=create&activity_type=updated",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
		// sx={{
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				// open={isSubmitting}
				open={
					isPendingProvinceDropDownList ||
					isSubmitting ||
					isPendingData ||
					isPendingActivityLogData ||
					isDeleting ||
					isPendingEmployeeDDLItems ||
					isLoadingData ||
					isArchive || isPending
				}
				testId={`${testId}-backdrop`}
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteFilingResourceField}
				cancelActionHandler={onCloseHandler}
				testId="filingResource-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<>
										<StandardPanel
											title="Resource Information"
											testId={`${testId}-resource-information`}
											showTitle={false}
											variant="outlined"
											topPadding={2}
										>
											{mobileTabOrSmallLaptopScreen && <EnhancedDropDownRHFWithLabel
												label="Load Filing Resource"
												selectName="filingResource"
												testId={`fr`}
												labelPosition={LabelPosition.Top}
												items={upadtedFilingResourceDDLItems ?? []}
												onSelectionChangeHandler={onFilingResourceSelectionchangeHandler}
												showEmpty
											/>}
											<EnhancedMultiDropDownRHFWithLabel
												selectName="role"
												label="Role"
												labelPosition={LabelPosition.Top}
												items={[
													{
														id: 1,
														label: "Contact Person",
														value: "6173ddf7-68e2-426f-b1be-9d1dd63eb49c",
													},
													{
														id: 2,
														label: "Issuer",
														value: "6173ddf7-68e2-426f-b1be-9d1dd63eb49d",
													},
													{
														id: 3,
														label: "Transmitter",
														value: "6173ddf7-68e2-426f-b1be-9d1dd63eb49e",
													},
													// { label: "Contact Person",value: 1},
													// {label: "Issuer",value: 2},
													// {label: "Transmitter",value: 3},
												]}
												selectedItems={["6173ddf7-68e2-426f-b1be-9d1dd63eb49c"]}
												isClearable={false}
												testId={`${testId}-selected-roles`}
												onSelectionChangeHandler={onSelectionChangeHandler}
											// required
											/>
											{/* <EnhancedDropDownRHFWithLabel
												selectName="employee"
												label="Employee"
												items={employeeDDLItems ?? []}
												onSelectionChangeHandler={onEmployeeSelectionChangeHandler}
												showEmpty
												testId={"employee-ddl"}
											/> */}
											<EnhancedDropDownRHFWithLabel
												selectName="effectiveFromYear"
												label="Effective From"
												labelPosition={LabelPosition.Top}
												items={yearItems ? sortDDLByLabel(yearItems) : []}
												testId={`${testId}-effective-from`}
											// required
											/>
											{/* <RHookFormTextFieldWithLabel
												label="Name"
												id="name"
												testId={`${testId}-name`}
												labelPosition={LabelPosition.Top}
												readOnly={firstNameDisabled}
												// required
											/> */}
											<RHookFormTextFieldWithLabel
												label="First Name"
												id="firstName"
												testId={`${testId}-name`}
												labelPosition={LabelPosition.Top}
												readOnly={firstNameDisabled}
											// required
											/>
											<RHookFormTextFieldWithLabel
												label="Last Name"
												id="lastName"
												testId={`${testId}-name`}
												labelPosition={LabelPosition.Top}
												readOnly={firstNameDisabled}
											// required
											/>
											<Box sx={{ pb: 2 }}></Box>
											<Grid
												container
												columnSpacing={0}
												size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
											>
												<Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
													<PhoneNumberRHF
														label="Phone"
														id="phone1"
														testId={`${testId}-phone`}
														labelPosition={LabelPosition.Top}
														labelColumns={6}
														fieldColumns={6}
													// required
													/>
													{/* <RHookFormTextFieldWithLabel
                        label="Phone"
                        id="phone1"
                        placeholder="(777) 777-777"
                        testId={`${testId}-phone`}
                        labelPosition={LabelPosition.Top}
                        labelColumns={6}
                        fieldColumns={6}
                        required
                      /> */}
												</Grid>
												<Grid
													size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}
												></Grid>
												<Grid size={{ xs: 3, sm: 3, md: 3, lg: 3, xl: 3 }}>
													<ExtensionNumberRHF
														label="Ext."
														id="ext"
														testId={`${testId}-ext`}
														labelPosition={LabelPosition.Top}
														labelColumns={6}
														fieldColumns={6}
													/>
													{/* <RHookFormTextFieldWithLabel
                        label="Ext."
                        id="ext"
                        testId={`${testId}-ext`}
                        labelPosition={LabelPosition.Top}
                        labelColumns={6}
                        fieldColumns={6}
                      /> */}
												</Grid>
											</Grid>
										</StandardPanel>
										{issuerSelected && (
											<StandardPanel
												id="issuerPanel"
												title="Other Details for Issuer"
												testId={`${testId}-other-details-issuer`}
												topPadding={2}
												showTitle={false}
												variant="outlined"
											>
												{selectedFillingResourceData &&
													selectedFillingResourceData.ownerSin1 &&
													selectedFillingResourceData.ownerSin1.charAt(2) ===
													"*" ? (
													<RHookFormTextFieldWithLabel
														label="Owner SIN 1"
														id="ownerSin1"
														testId={`${testId}-owner-sin-1`}
														labelPosition={LabelPosition.Top}
														// required
														labelColumns={4}
														fieldColumns={8}
														readOnly={
															selectedFillingResourceData ? true : false
														}
														value={"Unmask to view / edit"}
													/>
												) : (
													<SinNumberRHF
														label="Owner SIN 1"
														id="ownerSin1"
														testId={`${testId}-owner-sin-1`}
														labelPosition={LabelPosition.Top}
														// required
														labelColumns={4}
														fieldColumns={8}
													/>
												)}
												<SinNumberRHF
													label="Owner SIN 2"
													id="ownerSin2"
													testId={`${testId}-owner-sin-2`}
													labelPosition={LabelPosition.Top}
													labelColumns={4}
													fieldColumns={8}
												/>
												{/* <RHookFormTextFieldWithLabel
                      label="Owner SIN 1"
                      id="ownerSin1"
                      testId={`${testId}-owner-sin-1`}
                      labelPosition={LabelPosition.Top}
                      required
                    /> */}
												{/* <RHookFormTextFieldWithLabel
                      label="Owner SIN 2"
                      id="ownerSin2"
                      testId={`${testId}-owner-sin-2`}
                      labelPosition={LabelPosition.Top}
                    /> */}
											</StandardPanel>
										)}
										{transmitterSelected && (
											<>
												<StandardPanel
													title="Other Details for Transmitter"
													testId={`${testId}-other-details-transmitter`}
													topPadding={2}
													showTitle={false}
													variant="outlined"
												>
													{taxYear > 2023 ? (
														<RHookFormMaskedTextFieldWithLabel
															id="transmitterAccountNumber"
															label="Account Number"
															labelPosition={LabelPosition.Top}
															testId="accountNumber"
															// required={requiredAccountNumber}
															labelColumns={4}
															fieldColumns={8}
															placeholder={"000000000RP0000"}
															inputMaskPattern={"{000000000Ra0000}"}
														// defaultValue={"123123123RT0000"}
														/>
													) : (
														<></>
													)}
													{taxYear > 2023 ? (
														<RHookFormMaskedTextFieldWithLabel
															id="representativeId"
															label="Representative Id"
															labelPosition={LabelPosition.Top}
															testId="representativeId"
															// required={!requiredAccountNumber}
															labelColumns={4}
															fieldColumns={8}
															placeholder={"1234567"}
															inputMaskPattern={"0000000"}
														/>
													) : (
														<></>
													)}
													{taxYear <= 2023 ? (
														<RHookFormMaskedTextFieldWithLabel
															label="CRA Transmitter #"
															id="craTransmitter"
															testId={`${testId}-cra-transmitter-number`}
															labelPosition={LabelPosition.Top}
															placeholder={"MM123456"}
															// required
															inputMaskPattern={"MM000000"}
															labelColumns={4}
															fieldColumns={8}
														/>
													) : (
														<></>
													)}
													<RHookFormMaskedTextFieldWithLabel
														label="RQ Transmitter #"
														id="rqTransmitter"
														testId={`${testId}-rq-transmitter-number`}
														labelPosition={LabelPosition.Top}
														placeholder={"NP123456"}
														// required
														inputMaskPattern={"NP000000"}
														labelColumns={4}
														fieldColumns={8}
													/>
													{/* <RHookFormTextFieldWithLabel
                        label="CRA Transmitter #"
                        id="craTransmitter"
                        testId={`${testId}-cra-transmitter-number`}
                        labelPosition={LabelPosition.Top}
                        placeholder="MM123456"
                        required
                      /> */}
													{/* <RHookFormTextFieldWithLabel
                        label="RQ Transmitter #"
                        id="rqTransmitter"
                        testId={`${testId}-rq-transmitter-number`}
                        labelPosition={LabelPosition.Top}
                        placeholder="NP123456"
                      /> */}
													<EnhancedDropDownRHFWithLabel
														selectName="rl1Type"
														label="RL-1 Type"
														labelPosition={LabelPosition.Top}
														items={[
															{
																label: "If you are filing for yourself",
																value: "if you are filing for yourself",
															},
															{
																label:
																	"If you are filing on behalf of other filers",
																value:
																	"if you are filing on behalf of other filers",
															},
															{
																label:
																	"If you are filing for yourself and on behalf of other filers",
																value:
																	"if you are filing for yourself and on behalf of other filers",
															},
														]}
														testId={`${testId}-rl1-type`}
													/>
													<RHookFormTextFieldWithLabel
														label="Address"
														id="address"
														testId={`${testId}-transmitter-address`}
														labelPosition={LabelPosition.Top}
													// required
													/>
													<RHookFormTextFieldWithLabel
														label="City"
														id="city"
														testId={`${testId}-transmitter-city`}
														labelPosition={LabelPosition.Top}
													// required
													/>
													<EnhancedDropDownRHFWithLabel
														selectName="province"
														label="Province"
														// selectedItem={selectedProvince.shortCode}
														labelPosition={LabelPosition.Top}
														// required
														testId={`${testId}-transmitter-province`}
														items={provinceList ?? []}
													// onSelectionChangeHandler={
													//   onProvinceSelectionChangeHandler
													// }
													/>

													<RHookFormTextFieldWithLabel
														label="Country"
														id="country"
														testId={`${testId}-transmitter-country`}
														labelPosition={LabelPosition.Top}
														value="Canada"
														defaultValue={"Canada"}
														readOnly
													/>
													<PostCodeRHF
														label="Post Code"
														id="postCode"
														testId={`${testId}-transmitter-post-code`}
														labelPosition={LabelPosition.Top}
														// required
														labelColumns={4}
														fieldColumns={8}
													/>
													<RHookFormTextFieldWithLabel
														label="Contact Name"
														id="contactName"
														testId={`${testId}-transmitter-contact-name`}
														labelPosition={LabelPosition.Top}
													// required
													/>
													<EmailFieldRHF
														label="Email"
														id="email"
														testId={`${testId}-transmitter-email`}
														labelPosition={LabelPosition.Top}
														labelColumns={4}
														fieldColumns={8}
													// required
													/>
												</StandardPanel>
											</>
										)}
									</>
								}
								toolbar={
									// <StandardSubmitFormButtonsToolbar
									//   onResetClicked={onResetClicked}
									//   isSubmitting={false}
									//   testId="standard-form-submit-toolbar"
									// />
									<FilingResourceSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								toolbarPosition="bottom"
								showFullDiaplay={true}
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 3.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									FilingResourceList && upadtedFilingResourceListItems
										? upadtedFilingResourceListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPendingFilingResourceList}
								totalRecords={
									FilingResourceList && FilingResourceList.total
										? FilingResourceList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedFillingResourceData && id && selectedFillingResourceData.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveFilingResourceField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`${testId}-snackbar`}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
